import * as React from "react"
import * as MDT from "models/noMdt";
import { AppContext } from "AppContext";
import { ActionTypes } from "reducer/actions";
import { FormControlLabel, Grid, Switch } from "@mui/material";
import { PatientsBySiteChart } from "charts/mdt/PatientsBySiteChart";
import { FilterContext } from "models/core/filter";
import { AgeAtDiagnosisChart } from "charts/common/AgeAtDiagnosisChart";
import { SexChart } from "charts/common/SexChart";
import { ECOGChart } from "charts/common/ECOGChart";
import { ComorbiditiesChart } from "charts/common/ComorbiditiesChart";
import { FirstNationsStatusChart } from "charts/common/FirstNationsStatusChart";
import { SESChart } from "charts/common/SESChart";
import { RemotenessChart } from "charts/mdt/RemotenessChart";
import { FirstTreatmentChart } from "charts/noMdt/FirstTreatmentChart";
import { Link } from "react-scroll";
import { DashChip } from "components/common/DashChips";
import { OneYearSurvivalChart } from "charts/noMdt/OneYearSurvivalChart";
import { ThreeYearSurvivalChart } from "charts/noMdt/ThreeYearSurvivalChart";
import { FiveYearSurvivalChart } from "charts/noMdt/FiveYearSurvivalChart";
import { KMSurvivalChart } from "charts/noMdt/KMSurvivalChart";
import { NoMDTPatientsTrendChart } from "charts/noMdt/NoMDTPatientsTrendChart";
import Loader from "pages/shared/pageloader";


export function NoMdtDashboard() {
	const { state, dispatch } = React.useContext(AppContext);

	const [showFilterChips, setFilterChips] = React.useState(true);

	const addFilter: MDT.AddFilterFunc = async (key: keyof typeof MDT.DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string) => {
		const filteredDashboard = await MDT.computeChartData(MDT.addFilter(state.page.dashboard as MDT.IMDTDashboard)(key, value, context, labelOverride)); // state = what the other filter function returns 
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	const removeFilter: MDT.RemoveFilterFunc = async (key: keyof typeof MDT.DashboardFilterKeys, value?: string | number) => {

		const filteredDashboard = await MDT.computeChartData(MDT.removeFilter(state.page.dashboard as MDT.IMDTDashboard, key, value));
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	React.useEffect(() => {
		dispatch({ type: ActionTypes.UserWantsToLoadMdtDashboard });
		MDT.loadMdtDashboard(state.page.dashboard as MDT.IMDTDashboard)
			.then((value) => {
				dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value })
			});
		return () => {
			MDT.closeMdtDashboard();
		}
	}, []);

	React.useEffect(() => {

		const facility = state.page.dashboard.filters["Facility"];
		const primarySite = state.page.dashboard.filters["PrimarySite"];

		if (!facility || !primarySite)
			return;

		if (facility.value.length != 1 || primarySite.value.length != 1)
			return;

		MDT.loadSurvivalDashboard(state.page.dashboard as MDT.IMDTDashboard, facility.value[0], primarySite.value[0], state.page.dashboard.filters)
			.then((value) => {
				//dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value })
				dispatch({ type: ActionTypes.UserClickedOnChart, payload: value })
			});

		// return DashWorker.fetchSurvival(all);
	}, [state.page.dashboard.filters])


	if (state.page.loading || !state.page.dashboard.chartData)
		return <Loader />

	//Create function to show/hide filter chips
	const toggleChips = () => {
		if (showFilterChips) {
			setFilterChips(false);
		} else {
			setFilterChips(true);
		}
	};

	let activeFiltersArray = [];
	let filterArray = [];
	let chips = [];

	for (const key in state.page.dashboard.filters) {
		if (Object.prototype.hasOwnProperty.call(state.page.dashboard.filters, key)) {
			activeFiltersArray.push({ "key": key, "values": state.page.dashboard.filters[key].value, "parts": state.page.dashboard.filters[key].parts });
		}
	}

	for (const key in MDT.DashboardFilterKeys) {
		if (Object.prototype.hasOwnProperty.call(MDT.DashboardFilterKeys, key)) {
			filterArray.push({ "key": key, "label": MDT.DashboardFilterKeys[key].label });
		}
	}

	activeFiltersArray.forEach(a => {
		a.values.forEach(b => {
			chips.push(<DashChip key={a.key + b}
				removeFilterFn={removeFilter}
				filterName={filterArray.find(id => id.key == a.key).label}
				filterValue={b}
				filterId={a.key}
				filterParent={(a.parts.length > 1 ? a.parts[0] : null)}
			/>
			)
		})
	})

	const mdtData = state.page.dashboard.chartData as MDT.IChartData;

	const dashboard =
		<>
			<div style={{
				position: "sticky",
				top: "64px",
				left: "10px",
				zIndex: 2,
				backgroundColor: "white"
			}}>
				<nav>
					<Link activeClass="active" smooth spy to="Overview" style={{ paddingRight: "15px", color: "black" }}>
						Overview
					</Link>
					<Link activeClass="active" smooth spy to="TumourCx" style={{ paddingRight: "15px", color: "black" }}>
						Tumour Characteristics
					</Link>
					<Link activeClass="active" smooth spy to="PatientCx" style={{ paddingRight: "15px", color: "black" }}>
						Patient Characteristics
					</Link>
					<Link activeClass="active" smooth spy to="Quality" style={{ paddingRight: "15px", color: "black" }}>
						Quality Measures
					</Link>
					<Link activeClass="active" smooth spy to="Quality" style={{ paddingRight: "15px", color: "black" }}>
						Outcomes
					</Link>
				</nav>

				<div className="pull-right" style={{ position: "absolute", right: 0, top: "-5px" }}>
					<FormControlLabel
						control={
							<Switch
								checked={showFilterChips}
								onChange={_ => toggleChips()}
							/>
						}
						label="Show Filter Selections"
					/>
				</div>

				{(showFilterChips ? <div style={{ marginLeft: "20px", paddingBottom: "15px", width: "80%" }}>
					{chips}
				</div> : null)}
			</div>

			<Grid container spacing={2}>

				<Grid container id="Overview">
					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<NoMDTPatientsTrendChart
							plotDataPatients={mdtData.patientsByPeriod}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<PatientsBySiteChart
							plotData={mdtData.primarySite}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<FirstTreatmentChart
							plotData={mdtData.firstTreatment}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

				</Grid>

				{
					//Patient Characteristics
				}
				<Grid container id="PatientCx">
					<Grid item xs={11}>
						<h1 style={{ marginLeft: "10px" }}>Patient Characteristics</h1>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<AgeAtDiagnosisChart
							plotData={mdtData.ageRange}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<SexChart
							plotData={mdtData.sex}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<ECOGChart
							plotData={mdtData.ecogGroup}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<ComorbiditiesChart
							plotData={mdtData.comorbidities}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<FirstNationsStatusChart
							plotData={mdtData.indigenousGroup}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<SESChart
							plotData={mdtData.ses}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 3 : 11}>
						<RemotenessChart
							plotData={mdtData.remoteness}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					{/* <Grid item xs={window.innerWidth > 991 ? 3 : 11}>
				<HHSOfResidenceChart 
					plotData={mdtData.hhsOfResidence}
					activeFilters = {state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn ={removeFilter}
				/>
			</Grid>	 */}
				</Grid>

				{
					//Outcomes
				}
				<Grid container id="Outcomes">
					<Grid item xs={11}>
						<h1 style={{ marginLeft: "10px" }}>Outcomes</h1>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<OneYearSurvivalChart
							plotData={mdtData.oneYearSurvival}
							mdtFacilityRef={mdtData.facilities}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					<Grid item xs={window.innerWidth > 991 ? 4 : 11}>
						<ThreeYearSurvivalChart
							plotData={mdtData.threeYearSurvival}
							mdtFacilityRef={mdtData.facilities}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>

					{/* <Grid item xs={window.innerWidth > 991 ? 4 : 11}>
				<FiveYearSurvivalChart 
					plotData={mdtData.fiveYearSurvival}
					mdtFacilityRef = {mdtData.facilities}
					activeFilters = {state.page.dashboard.filters}
					addFilterFn={addFilter}
					removeFilterFn ={removeFilter}
				/>
			</Grid>	 */}

					<Grid item xs={window.innerWidth > 991 ? 12 : 11}>
						<KMSurvivalChart
							plotData={mdtData.kmSurvival}
							mdtFacilityRef={mdtData.facilities}
							activeFilters={state.page.dashboard.filters}
							addFilterFn={addFilter}
							removeFilterFn={removeFilter}
						/>
					</Grid>
				</Grid>

			</Grid>
		</>

	return (
		dashboard
	)


} 